import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { fetchAvailableCompanies } from '../../../store/actions';

const ALLOWED_MONTHS_RANGE = 1;
const ERROR_CODES = {
  START_DATE_OUT_OF_ALLOWED_RANGE: 'START_DATE_OUT_OF_ALLOWED_RANGE',
  END_DATE_OUT_OF_ALLOWED_RANGE: 'END_DATE_OUT_OF_ALLOWED_RANGE',
  END_DATE_BEFORE_OR_EQUAL_START_DATE: 'END_DATE_BEFORE_OR_EQUAL_START_DATE',
  DUPLICATE_ORDER_NUMBER_IN_FILE: 'DUPLICATE_ORDER_NUMBER_IN_FILE',
  ORDER_ALREADY_EXISTS: 'ORDER_ALREADY_EXISTS',
  MONITORING_ORDER_ALREADY_EXPIRED: 'MONITORING_ORDER_ALREADY_EXPIRED',
};
const ERROR_MESSAGES = {
  START_DATE_OUT_OF_ALLOWED_RANGE: 'Start date must be within +/-1 month from now (UTC)',
  END_DATE_OUT_OF_ALLOWED_RANGE: 'End date must be within +/-1 month from now (UTC)',
  END_DATE_BEFORE_OR_EQUAL_START_DATE: 'End date must be after Start date',
  DUPLICATE_ORDER_NUMBER_IN_FILE: (id) => `Duplicate Order number in file: ${id}`,
};

const cleanPlate = (countryCode, plate) => {
  const cc = (countryCode || '').toString().trim();
  const raw = (plate || '').toString().trim();
  if (!cc) return raw;
  const prefix = `${cc} `;
  return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
};

const normalizeDateTimeForBackend = (value) => {
  if (!value) return value;
  const m = moment.utc(value);
  if (!m.isValid()) {
    if (typeof value === 'string') {
      let s = value.replace(/Z$/, '');
      s = s.replace(/\.\d+$/, '');
      return s;
    }
    return value;
  }
  return m.format('YYYY-MM-DD[T]HH:mm:ss');
};

const normalizeFieldPath = (fieldPath) => (fieldPath || '')
  .toString()
  .replace(/^orders\[\d+]\./, '');

const isDateFieldPath = (fieldPath) => {
  const normalized = normalizeFieldPath(fieldPath);
  return normalized.startsWith('startDate') || normalized.startsWith('endDate');
};

const isInternalIdFieldPath = (fieldPath) => {
  const normalized = normalizeFieldPath(fieldPath);
  return normalized.startsWith('internalId') || normalized.startsWith('orderNumber');
};

const normalizeErrorCode = (code) => (code || '').toString().trim().toUpperCase();

const hasErrorCode = (error, code) => normalizeErrorCode(error?.code) === code;

const isDuplicateOrderNumberInFileError = (error) => {
  if (hasErrorCode(error, ERROR_CODES.DUPLICATE_ORDER_NUMBER_IN_FILE)) return true;
  if (!isInternalIdFieldPath(error?.fieldPath)) return false;
  const message = (error?.message || '').toString().trim().toLowerCase();
  return message.startsWith('duplicate order number in file');
};

const isOrderAlreadyExistsWarning = (error) => hasErrorCode(error, ERROR_CODES.ORDER_ALREADY_EXISTS);

const isMonitoringOrderAlreadyExpiredError = (error) => (
  hasErrorCode(error, ERROR_CODES.MONITORING_ORDER_ALREADY_EXPIRED)
);

const isNonBlockingBackendError = (error) => isOrderAlreadyExistsWarning(error);

const getErrorDedupKey = (error) => {
  const field = normalizeFieldPath(error?.fieldPath);
  const code = normalizeErrorCode(error?.code);
  if (code) return `${field}|${code}`;
  const message = (error?.message || '').toString().trim();
  return `${field}|${message}`;
};

const normalizeMonitoringOrderCase = (error) => {
  if (!error || typeof error.message !== 'string') return error;
  const normalizedMessage = error.message
    .replace(/\bmonitoring orders\b/gi, 'Monitoring Orders')
    .replace(/\bmonitoring order\b/gi, 'Monitoring Order');
  if (normalizedMessage === error.message) return error;
  return {
    ...error,
    message: normalizedMessage,
  };
};

const addErrorToMap = (map, rowIndex, error) => {
  if (!map.has(rowIndex)) map.set(rowIndex, []);
  map.get(rowIndex).push(error);
};

const makeInlineError = (rowIndex, fieldPath, message, code) => ({
  orderIndex: rowIndex,
  fieldPath,
  message,
  code,
});

const toUtcMoment = (value) => {
  if (!value) return null;
  const m = moment.utc(value);
  return m.isValid() ? m : null;
};

const getOrderInternalId = (order) => (order?.internalId || '').toString().trim();

const isOutsideAllowedRange = (date, lowerBound, upperBound) => (
  Boolean(date && (date.isBefore(lowerBound) || date.isAfter(upperBound)))
);

const getRowsWithDuplicateInternalId = (orders) => {
  const idsToRows = new Map();
  orders.forEach((order, idx) => {
    const id = getOrderInternalId(order);
    if (!id) return;
    if (!idsToRows.has(id)) idsToRows.set(id, []);
    idsToRows.get(id).push(idx);
  });

  const duplicatedRows = new Set();
  idsToRows.forEach((rows) => {
    if (rows.length > 1) {
      rows.forEach((idx) => duplicatedRows.add(idx));
    }
  });

  return duplicatedRows;
};

const addInlineDateErrors = ({
  map, rowIndex, start, end, lowerBound, upperBound,
}) => {
  if (isOutsideAllowedRange(start, lowerBound, upperBound)) {
    addErrorToMap(
      map,
      rowIndex,
      makeInlineError(
        rowIndex,
        'startDate',
        ERROR_MESSAGES.START_DATE_OUT_OF_ALLOWED_RANGE,
        ERROR_CODES.START_DATE_OUT_OF_ALLOWED_RANGE,
      ),
    );
  }

  if (start && end && !end.isAfter(start)) {
    addErrorToMap(
      map,
      rowIndex,
      makeInlineError(
        rowIndex,
        'endDate',
        ERROR_MESSAGES.END_DATE_BEFORE_OR_EQUAL_START_DATE,
        ERROR_CODES.END_DATE_BEFORE_OR_EQUAL_START_DATE,
      ),
    );
  }

  const effectiveEnd = end || start;
  if (isOutsideAllowedRange(effectiveEnd, lowerBound, upperBound)) {
    addErrorToMap(
      map,
      rowIndex,
      makeInlineError(
        rowIndex,
        'endDate',
        ERROR_MESSAGES.END_DATE_OUT_OF_ALLOWED_RANGE,
        ERROR_CODES.END_DATE_OUT_OF_ALLOWED_RANGE,
      ),
    );
  }
};

const addInlineDuplicateError = ({
  map, rowIndex, order, duplicatedRows,
}) => {
  if (!duplicatedRows.has(rowIndex)) return;
  addErrorToMap(
    map,
    rowIndex,
    makeInlineError(
      rowIndex,
      'internalId',
      ERROR_MESSAGES.DUPLICATE_ORDER_NUMBER_IN_FILE(getOrderInternalId(order)),
      ERROR_CODES.DUPLICATE_ORDER_NUMBER_IN_FILE,
    ),
  );
};

const buildBackendErrorsByIndex = (previewErrors) => {
  const map = new Map();
  (previewErrors || []).forEach((err) => {
    if (isNonBlockingBackendError(err)) return;
    const idx = Number(err?.orderIndex);
    if (!Number.isInteger(idx) || idx < 0) return;
    if (!map.has(idx)) map.set(idx, []);
    map.get(idx).push(err);
  });
  return map;
};

const buildInlineErrorsByIndex = (sourceOrders) => {
  const map = new Map();
  const orders = Array.isArray(sourceOrders) ? sourceOrders : [];
  if (!orders.length) return map;

  const nowUtc = moment.utc();
  const lowerBound = nowUtc.clone().subtract(ALLOWED_MONTHS_RANGE, 'month');
  const upperBound = nowUtc.clone().add(ALLOWED_MONTHS_RANGE, 'month');
  const duplicatedRows = getRowsWithDuplicateInternalId(orders);

  orders.forEach((order, rowIndex) => {
    const start = toUtcMoment(order?.startDate);
    const end = toUtcMoment(order?.endDate);

    addInlineDateErrors({
      map,
      rowIndex,
      start,
      end,
      lowerBound,
      upperBound,
    });
    addInlineDuplicateError({
      map,
      rowIndex,
      order,
      duplicatedRows,
    });
  });

  return map;
};

const filterBackendErrorsForTouchedFields = (backendErrors, touchedFields) => {
  const hasDateTouched = Boolean(touchedFields.startDate || touchedFields.endDate);
  const hasInternalIdTouched = Boolean(touchedFields.internalId);

  return backendErrors.filter((err) => {
    if (hasDateTouched && isDateFieldPath(err?.fieldPath)) return false;
    if (
      hasInternalIdTouched
      && (
        isDuplicateOrderNumberInFileError(err)
        || isOrderAlreadyExistsWarning(err)
        || isMonitoringOrderAlreadyExpiredError(err)
      )
    ) {
      return false;
    }
    return true;
  });
};

const mergeErrorsDeduplicated = (errors) => {
  const merged = [];
  const seen = new Set();
  errors.forEach((rawErr) => {
    const err = normalizeMonitoringOrderCase(rawErr);
    const key = getErrorDedupKey(err);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(err);
  });
  return merged;
};

const buildEffectiveErrorsByIndex = ({
  backendErrorsByIndex,
  inlineErrorsByIndex,
  touchedFieldsByRow,
}) => {
  const map = new Map();
  const rowIndices = new Set([
    ...backendErrorsByIndex.keys(),
    ...inlineErrorsByIndex.keys(),
  ]);

  rowIndices.forEach((idx) => {
    const backendErrors = backendErrorsByIndex.get(idx) || [];
    const inlineErrors = inlineErrorsByIndex.get(idx) || [];
    const touchedFields = touchedFieldsByRow[idx] || {};
    const filteredBackendErrors = filterBackendErrorsForTouchedFields(backendErrors, touchedFields);
    const merged = mergeErrorsDeduplicated([...filteredBackendErrors, ...inlineErrors]);

    if (merged.length) {
      map.set(idx, merged);
    }
  });

  return map;
};

export const normalizeOrdersForImport = (orders) => (
  (Array.isArray(orders) ? orders : []).map((order) => {
    const next = { ...order };

    if (Array.isArray(next.vehicles)) {
      next.vehicles = next.vehicles
        .filter((v) => v && (v.licensePlate || v.countryCode))
        .map((v) => {
          const cc = (v?.countryCode || '').toString().trim();
          return {
            ...v,
            licensePlate: cleanPlate(cc, v?.licensePlate || ''),
          };
        });
    }

    if (next.startDate) next.startDate = normalizeDateTimeForBackend(next.startDate);
    if (next.endDate) next.endDate = normalizeDateTimeForBackend(next.endDate);

    if (Array.isArray(next.stops)) {
      next.stops = next.stops.map((stop) => ({
        ...stop,
        startDate: stop?.startDate ? normalizeDateTimeForBackend(stop.startDate) : stop?.startDate,
        endDate: stop?.endDate ? normalizeDateTimeForBackend(stop.endDate) : stop?.endDate,
      }));
    }

    return next;
  })
);

const useImportOrderPreview = ({ visible, step }) => {
  const dispatch = useDispatch();
  const [preview, setPreview] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editedPreview, setEditedPreview] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [touchedFieldsByRow, setTouchedFieldsByRow] = useState({});
  const [hasUnvalidatedChanges, setHasUnvalidatedChanges] = useState(false);

  const fileLevelErrors = useMemo(() => (
    (previewErrors || []).filter(
      (err) => Number(err?.orderIndex) === -1 && !isNonBlockingBackendError(err),
    )
  ), [previewErrors]);

  const sourceOrders = useMemo(() => (
    editedPreview && editedPreview.length ? editedPreview : preview
  ), [editedPreview, preview]);

  const backendErrorsByIndex = useMemo(
    () => buildBackendErrorsByIndex(previewErrors),
    [previewErrors],
  );

  const inlineErrorsByIndex = useMemo(
    () => buildInlineErrorsByIndex(sourceOrders),
    [sourceOrders],
  );

  const errorsByIndex = useMemo(
    () => buildEffectiveErrorsByIndex({
      backendErrorsByIndex,
      inlineErrorsByIndex,
      touchedFieldsByRow,
    }),
    [backendErrorsByIndex, inlineErrorsByIndex, touchedFieldsByRow],
  );

  useEffect(() => {
    if (preview && preview.length) {
      const keys = preview
        .map((_, idx) => idx)
        .filter((idx) => !backendErrorsByIndex.has(idx));
      setSelectedRowKeys(keys);
      setEditedPreview(preview);
      setTouchedFieldsByRow({});
      setHasUnvalidatedChanges(false);
      setPage(1);
      return;
    }
    setSelectedRowKeys([]);
    setEditedPreview([]);
    setTouchedFieldsByRow({});
    setHasUnvalidatedChanges(false);
    setPage(1);
  }, [preview, backendErrorsByIndex]);

  useEffect(() => {
    setSelectedRowKeys((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      const filtered = prev.filter((idx) => !errorsByIndex.has(idx));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [errorsByIndex]);

  useEffect(() => {
    if (visible && step === 2) {
      setVehiclesLoading(true);
      dispatch(fetchAvailableCompanies())
        .then((resp) => {
          const direct = resp?.payload?.data;
          let list = [];
          if (Array.isArray(direct)) {
            list = direct;
          }

          const finishWith = (vehicles) => {
            const seen = new Set();
            const unique = vehicles.filter((v) => {
              const rawPlate = v?.licencePlateNumber || '';
              const cc = '';
              const plate = cleanPlate(cc, rawPlate);
              const key = `${cc}#${plate}`;
              if (!plate && !cc) return false;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            setAllVehicles(unique);
            setVehiclesLoading(false);
          };

          if (!list.length) {
            finishWith([]);
            return null;
          }

          const collected = [];
          list.forEach((company) => {
            if (!company) return;
            const embedded = Array.isArray(company.vehicles) ? company.vehicles : [];
            if (embedded.length) {
              embedded.forEach((vehicle) => {
                if (vehicle?.licencePlateNumber) {
                  collected.push(vehicle);
                }
              });
            }
          });

          finishWith(collected.length ? collected : []);
          return null;
        })
        .catch(() => {
          setAllVehicles([]);
          setVehiclesLoading(false);
        });
    }
  }, [visible, step, dispatch]);

  const handleFieldChange = useCallback((rowIndex, field, value) => {
    setHasUnvalidatedChanges(true);
    setTouchedFieldsByRow((prev) => ({
      ...prev,
      [rowIndex]: {
        ...(prev[rowIndex] || {}),
        [field]: true,
      },
    }));
    setEditedPreview((prev) => {
      const list = Array.isArray(prev) && prev.length ? [...prev] : [...preview];
      const row = { ...(list[rowIndex] || {}) };
      row[field] = value;
      list[rowIndex] = row;
      return list;
    });
  }, [preview]);

  const tableData = useMemo(() => (
    (preview || []).map((item, idx) => ({ key: idx, ...item }))
  ), [preview]);

  const handleTableChange = useCallback((pagination) => {
    const { current, pageSize: nextPageSize } = pagination || {};
    if (typeof nextPageSize === 'number' && nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    if (typeof current === 'number' && current !== page) {
      setPage(current);
    }
  }, [page, pageSize]);

  const clearPreviewState = useCallback(() => {
    setPreview([]);
    setPreviewErrors([]);
    setSelectedRowKeys([]);
    setEditedPreview([]);
    setTouchedFieldsByRow({});
    setHasUnvalidatedChanges(false);
    setAllVehicles([]);
    setVehiclesLoading(false);
    setPage(1);
    setPageSize(5);
  }, []);

  const applyPreviewData = useCallback((data) => {
    setPreview(Array.isArray(data?.orders) ? data.orders : []);
    setPreviewErrors(Array.isArray(data?.errors) ? data.errors : []);
    setTouchedFieldsByRow({});
    setHasUnvalidatedChanges(false);
  }, []);

  const getOrdersToImport = useCallback(() => {
    const source = sourceOrders;
    return selectedRowKeys
      .filter((idx) => !errorsByIndex.has(idx))
      .map((idx) => source[idx])
      .filter(Boolean);
  }, [sourceOrders, selectedRowKeys, errorsByIndex]);

  const getAllOrdersForValidation = useCallback(() => {
    const source = editedPreview && editedPreview.length ? editedPreview : preview;
    return (Array.isArray(source) ? source : []).filter(Boolean);
  }, [editedPreview, preview]);

  return {
    preview,
    previewErrors,
    fileLevelErrors,
    errorsByIndex,
    editedPreview,
    selectedRowKeys,
    allVehicles,
    vehiclesLoading,
    page,
    pageSize,
    tableData,
    hasUnvalidatedChanges,
    setSelectedRowKeys,
    handleFieldChange,
    handleTableChange,
    clearPreviewState,
    applyPreviewData,
    getOrdersToImport,
    getAllOrdersForValidation,
  };
};

export default useImportOrderPreview;
